// v2 rock — a single angular outcrop: a tall tilted shard of plain grey stone
// breaking out of the ground, a slab leaning against its flank, a broken foot
// behind and a chip of scree at the base, so it reads as craggy bedrock, not a
// smooth blob. Each variant turns the outcrop and re-leans the shard
// deterministically. Ported 1:1 from age-of-mcp's v2/rock.ts, using the
// library's shared `mineral` (h01, lerp) helpers.
import * as THREE from "three";
import { defineModel, mineral, type Part } from "../../lib";

function rockParts(v: number): Part[] {
  const seed = v * 7 + 5;
  const yaw = v * 2.1;                                  // face elsewhere per variant
  const grey = ["#7b7f88", "#84878e", "#73767e"][v % 3]!;
  const dark = "#62656d";
  const light = "#8f939b";
  const lean = mineral.lerp(0.08, 0.2, mineral.h01(seed, 1));   // main shard tilt
  const parts: Part[] = [
    // main shard — a tall stretched tetrahedron, the outcrop's spike
    { geometry: new THREE.TetrahedronGeometry(0.3, 0), color: grey,
      position: [0, 0.2, 0],
      rotation: [lean, yaw + 0.7, -lean * 0.6],
      scale: [1.05, 1.95, 0.95] },
    // a flat slab leaning against its flank
    { geometry: new THREE.BoxGeometry(0.34, 0.44, 0.2), color: light,
      position: [0.17, 0.15, 0.07],
      rotation: [0.1, yaw + 0.25, -0.38] },
    // broken foot — a squat shard on the far side
    { geometry: new THREE.TetrahedronGeometry(0.22, 0), color: dark,
      position: [-0.18, 0.09, -0.05],
      rotation: [0.5, yaw + 2.4, 0.2],
      scale: [1.2, 0.85, 1.1] },
    // a chip of scree at the base
    { geometry: new THREE.IcosahedronGeometry(0.09, 0), color: grey,
      position: [0.05, 0.05, -0.22],
      rotation: [0, yaw, 0.3],
      scale: [1.2, 0.7, 1.0] },
  ];
  return parts;
}

export const rockV2 = defineModel({
  id: "rock-v2",
  batchId: "fable",
  name: "Rock (v2)",
  tags: ["scenery","rock","low-poly","age-of-mcp","fable"],
  summary: "Low-poly rock.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.42,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => rockParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => rockParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => rockParts(2) },
  ],
  animations: [],
});
